import {
  SortOrder,
  buildCriterionExpression,
  buildFieldSortCriterion,
  buildRelevanceSortCriterion,
  type SortCriterion,
} from '@coveo/headless-react/ssr';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import {
  FIELD_SORT_DATA,
  POPULARITY_SORT_VALUE,
  RATING_SORT_VALUE,
  RELEVANCE_SORT_VALUE,
} from '@lib/coveo/sortCriteriaData';

/**
 * SearchV2 sort criteria, built with Headless 3 (`@coveo/headless-react/ssr`)
 * builders from the shared sort table in `@lib/coveo/sortCriteriaData` — the
 * same data the CSR arm (`@lib/coveo/sortCriteria`) builds from, so the two
 * experiment arms cannot drift on sort semantics.
 *
 * `relevance`, `popularity` and `rating` intentionally share the relevance
 * criterion — they're distinguished by the ranking context flags, not the sort.
 */
export type SortOption = { value: string; criterion: SortCriterion };

export const RELEVANCE_SORT: SortOption = {
  value: RELEVANCE_SORT_VALUE,
  criterion: buildRelevanceSortCriterion(),
};

export const POPULARITY_SORT: SortOption = {
  value: POPULARITY_SORT_VALUE,
  criterion: buildRelevanceSortCriterion(),
};

export const RATING_SORT: SortOption = {
  value: RATING_SORT_VALUE,
  criterion: buildRelevanceSortCriterion(),
};

const FIELD_SORT_CRITERIA: SortOption[] = FIELD_SORT_DATA.map(
  ({ value, field, order }) => ({
    value,
    criterion: buildFieldSortCriterion(
      field,
      order === 'ascending' ? SortOrder.Ascending : SortOrder.Descending
    ),
  })
);

export const SEARCH_SORT_CRITERIA: SortOption[] = [
  RELEVANCE_SORT,
  POPULARITY_SORT,
  RATING_SORT,
  ...FIELD_SORT_CRITERIA,
];

export const LISTING_SORT_CRITERIA: SortOption[] = [
  POPULARITY_SORT,
  RATING_SORT,
  ...FIELD_SORT_CRITERIA,
];

export const getSortCriteriaForSearchHub = (
  searchHub: string | undefined
): SortOption[] =>
  searchHub === CoveoSearchHub.SEARCH
    ? SEARCH_SORT_CRITERIA
    : LISTING_SORT_CRITERIA;

export const RELEVANCY_CRITERION_EXPRESSION = buildCriterionExpression(
  RELEVANCE_SORT.criterion
);

/**
 * Serialize a sort criterion to its Coveo expression string — used to match a
 * URL-restored sort (`state.sortCriteria`, a string) back to a SortOption.
 * Wraps the Headless builder so components import it from here, not `@coveo/*`.
 */
export const criterionToExpression = (criterion: SortCriterion): string =>
  buildCriterionExpression(criterion);

/**
 * Whether a restored sort leaves ranking to the relevancy-based modes, which
 * the URL cannot tell apart (see the ranking context flags above).
 */
export const isRelevancySortCriteria = (sortCriteria: string | undefined) =>
  !sortCriteria || sortCriteria === RELEVANCY_CRITERION_EXPRESSION;
